import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { IPAMStatCard } from './IPAMStatCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Network, Plus, Eye, Edit, Trash2, Copy, AlertTriangle, Globe,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../../hooks/usePermissions';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkDeleteBar from '../../components/BulkDeleteBar';

const initialFormData = {
  vlan_id: '',
  site_id: '',
  network: '',
  mask: '',
  gateway: '',
  description: ''
};

const SubnetsPage = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions('ipam.subnets');
  const [subnets, setSubnets] = useState([]);
  const [vlans, setVlans] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  
  // Bulk selection
  const {
    selectedIds,
    isAllSelected,
    isSomeSelected,
    isSelected,
    toggleRow,
    toggleAll,
    clearSelection
  } = useBulkSelection(subnets);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'network', dir: 'asc' });
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    search: '',
    vlan_id: '',
    site_id: '',
    hasIps: '' // 'with', 'without', or '' for all
  });
  
  // Stats
  const [stats, setStats] = useState({ total: 0, with_ips: 0, empty: 0, total_ips: 0 });
  
  // CRUD State
  const [modalOpen, setModalOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [copyingFrom, setCopyingFrom] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchOptions = async () => {
    try {
      const [vlansRes, sitesRes] = await Promise.all([
        api.get('/ipam/vlans/all'),
        api.get('/config/sites?limit=100')
      ]);
      setVlans(vlansRes.data || []);
      setSites(sitesRes.data?.data || sitesRes.data || []);
    } catch (error) {
      console.error('Error loading options:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/ipam/subnets/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSubnets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        page: pagination.page, 
        limit: pagination.limit,
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      
      if (columnFilters.search) params.append('search', columnFilters.search);
      if (columnFilters.vlan_id && columnFilters.vlan_id !== 'all') {
        params.append('vlan_id', columnFilters.vlan_id);
      }
      if (columnFilters.site_id && columnFilters.site_id !== 'all') {
        params.append('site_id', columnFilters.site_id);
      }

      const response = await api.get(`/ipam/subnets?${params}`);
      setSubnets(response.data.data || []);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar Subnets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOptions(); fetchStats(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSubnets(); }, [pagination.page, sortConfig]);
  
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchSubnets();
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);

  const handleFilterChange = (field, value) => {
    setColumnFilters(prev => ({ ...prev, [field]: value }));
  };

  // CRUD Handlers
  const handleCreate = () => {
    setEditingItem(null);
    setCopyingFrom(null);
    setFormData(initialFormData);
    setModalOpen(true);
  };

  const handleEdit = (subnet) => {
    setEditingItem(subnet);
    setCopyingFrom(null);
    setFormData({
      vlan_id: subnet.vlan_id?.toString() || '',
      site_id: subnet.site_id?.toString() || '',
      network: subnet.network || '',
      mask: subnet.mask?.toString() || '',
      gateway: subnet.gateway || '',
      description: subnet.description || ''
    });
    setModalOpen(true);
  };

  const handleCopy = (subnet) => {
    setCopyingFrom(`${subnet.network}/${subnet.mask}`);
    setEditingItem(null);
    setFormData({
      vlan_id: subnet.vlan_id?.toString() || '',
      site_id: subnet.site_id?.toString() || '',
      network: '', // Unique - empty
      mask: subnet.mask?.toString() || '',
      gateway: '', // Unique - empty
      description: subnet.description || ''
    });
    setCopyModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.network.trim() || !formData.mask) {
      toast.error('Red y Máscara son requeridos');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        vlan_id: formData.vlan_id ? parseInt(formData.vlan_id) : null,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        mask: parseInt(formData.mask),
        gateway: formData.gateway || null
      };

      if (editingItem) {
        await api.put(`/ipam/subnets/${editingItem.id}`, payload);
        toast.success('Subnet actualizada');
      } else {
        await api.post('/ipam/subnets', payload);
        toast.success('Subnet creada');
      }

      setModalOpen(false);
      setCopyModalOpen(false);
      setEditingItem(null);
      setCopyingFrom(null);
      setFormData(initialFormData);
      fetchSubnets();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/ipam/subnets/${deleteConfirm.id}`);
      toast.success('Subnet eliminada');
      setDeleteConfirm(null);
      fetchSubnets();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Filter Subnets based on hasIps filter (Stats Card click)
  const filteredSubnets = subnets.filter(subnet => {
    if (columnFilters.hasIps === 'with') {
      return (subnet.used_ips || 0) > 0;
    } else if (columnFilters.hasIps === 'without') {
      return (subnet.used_ips || 0) === 0;
    }
    return true;
  });

  // Utilization color
  const getUtilizationColor = (util) => {
    if (util >= 90) return 'text-red-500';
    if (util >= 75) return 'text-yellow-500';
    return 'text-green-500';
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="subnets-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-2xl font-bold text-foreground">Subnets</h1>
              <p className="text-sm text-muted-foreground">Segmentos de Red IP</p>
            </div>
          </div>
          {canCreate && (
            <Button onClick={handleCreate} data-testid="create-subnet-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nueva Subnet
            </Button>
          )}
        </div>

        {/* Stats Cards - Clickeable para filtrar */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <IPAMStatCard icon={Globe} value={stats.total} label="Total Subnets"
            selected={columnFilters.hasIps === ''}
            onClick={() => { setColumnFilters(prev => ({ ...prev, hasIps: '' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
          <IPAMStatCard icon={Network} value={stats.with_ips} label="Con IPs" iconClassName="text-green-500"
            selected={columnFilters.hasIps === 'with'}
            onClick={() => { setColumnFilters(prev => ({ ...prev, hasIps: 'with' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
          <IPAMStatCard icon={AlertTriangle} value={stats.empty} label="Sin IPs" iconClassName="text-yellow-500"
            selected={columnFilters.hasIps === 'without'}
            onClick={() => { setColumnFilters(prev => ({ ...prev, hasIps: 'without' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
          <IPAMStatCard icon={Layers} value={stats.total_ips} label="IPs Registradas" />
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full table-subtle">
            <thead>
              <tr className="border-b border-border/50">
                {canDelete && (
                  <th className="w-10 p-3">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Seleccionar todos"
                      className={isSomeSelected ? 'opacity-50' : ''}
                    />
                  </th>
                )}
                <th 
                  className="text-left p-3 font-medium text-xs uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('network')}
                >
                  <div className="flex items-center gap-1">
                    SUBNET
                    <SortIcon field="network" />
                  </div>
                </th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wider">
                  VLAN
                </th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wider">
                  SITE
                </th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wider">
                  GATEWAY
                </th>
                <th className="text-center p-3 font-medium text-xs uppercase tracking-wider">
                  UTILIZACIÓN
                </th>
                <th className="w-28 text-right p-3 font-medium text-xs uppercase tracking-wider">
                  ACCIONES
                </th>
              </tr>
              <tr className="border-b border-border/30 bg-muted/30">
                {canDelete && <th className="p-2"></th>}
                <th className="p-2">
                  <Input
                    placeholder="Buscar red..."
                    value={columnFilters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="h-8 text-xs"
                  />
                </th>
                <th className="p-2">
                  <Select value={columnFilters.vlan_id} onValueChange={(v) => handleFilterChange('vlan_id', v)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {vlans.map((v) => (
                        <SelectItem key={v.id} value={String(v.id)}>
                          {v.vlan_number} - {v.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </th>
                <th className="p-2">
                  <Select value={columnFilters.site_id} onValueChange={(v) => handleFilterChange('site_id', v)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {sites.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>{s.site}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </th>
                <th className="p-2"></th>
                <th className="p-2"></th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {loading ? (
                <tr>
                  <td colSpan={canDelete ? 7 : 6} className="text-center p-8 text-muted-foreground">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      Cargando...
                    </div>
                  </td>
                </tr>
              ) : filteredSubnets.length === 0 ? (
                <tr>
                  <td colSpan={canDelete ? 7 : 6} className="text-center p-8">
                    <Globe className="h-12 w-12 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-muted-foreground">No hay Subnets</p>
                  </td>
                </tr>
              ) : (
                filteredSubnets.map((subnet) => (
                  <tr key={subnet.id} className={`hover:bg-muted/30 ${isSelected(subnet.id) ? 'bg-primary/5' : ''}`} data-testid={`subnet-row-${subnet.id}`}>
                    {canDelete && (
                      <td className="p-3 w-10">
                        <Checkbox
                          checked={isSelected(subnet.id)}
                          onCheckedChange={() => toggleRow(subnet.id)}
                          aria-label={`Seleccionar subnet ${subnet.cidr}`}
                        />
                      </td>
                    )}
                    <td className="p-3">
                      <div>
                        <span className="font-mono font-bold text-primary">{subnet.cidr || `${subnet.network}/${subnet.mask}`}</span>
                        {subnet.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{subnet.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      {subnet.vlan_number ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted">
                          {subnet.vlan_number} - {subnet.vlan_name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground">{subnet.site_name || '-'}</td>
                    <td className="p-3">
                      {subnet.gateway ? (
                        <span className="font-mono text-sm">{subnet.gateway}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex flex-col items-center">
                        <span className={`font-bold ${getUtilizationColor(subnet.utilization || 0)}`}>
                          {subnet.utilization || 0}%
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {subnet.occupied_ips || 0} / {subnet.total_hosts || 0}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 w-28">
                      <div className="flex justify-end gap-0.5">
                        <Link to={`/ipam/subnets/${subnet.id}`}>
                          <button className="action-btn" title="Ver detalle">
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                        </Link>
                        {canUpdate && (
                          <button onClick={() => handleEdit(subnet)} className="action-btn action-btn-edit" title="Editar">
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canCreate && (
                          <button onClick={() => handleCopy(subnet)} className="action-btn action-btn-copy" title="Copiar">
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => setDeleteConfirm(subnet)} className="action-btn-delete" title="Eliminar">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
              <span className="text-sm text-muted-foreground">
                {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">Página {pagination.page} de {pagination.pages}</span>
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.pages}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar Subnet' : 'Nueva Subnet'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Red *</Label>
                  <Input
                    value={formData.network}
                    onChange={(e) => handleInputChange('network', e.target.value)}
                    placeholder="192.168.1.0"
                    className="mt-1.5 font-mono"
                  />
                </div>
                <div>
                  <Label>Máscara (CIDR) *</Label>
                  <Input
                    type="number"
                    min="8"
                    max="30"
                    value={formData.mask}
                    onChange={(e) => handleInputChange('mask', e.target.value)}
                    placeholder="24"
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div>
                <Label>Gateway</Label>
                <Input
                  value={formData.gateway}
                  onChange={(e) => handleInputChange('gateway', e.target.value)}
                  placeholder="192.168.1.1"
                  className="mt-1.5 font-mono"
                />
              </div>
              <div>
                <Label>VLAN</Label>
                <Select value={formData.vlan_id || "none"} onValueChange={(v) => handleInputChange('vlan_id', v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Sin VLAN" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin VLAN</SelectItem>
                    {vlans.map((v) => (
                      <SelectItem key={v.id} value={String(v.id)}>
                        {v.vlan_number} - {v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Site</Label>
                <Select value={formData.site_id || "none"} onValueChange={(v) => handleInputChange('site_id', v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Sin Site" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin Site</SelectItem>
                    {sites.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.site}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Descripción</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Descripción opcional"
                  className="mt-1.5"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : (editingItem ? 'Guardar' : 'Crear')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Copy Modal */}
        <Dialog open={copyModalOpen} onOpenChange={setCopyModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="h-5 w-5 text-primary" />
                Copiar Subnet
              </DialogTitle>
              {copyingFrom && <p className="text-sm text-muted-foreground">Copiando desde: {copyingFrom}</p>}
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">Campos únicos (obligatorio)</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Red *</Label>
                  <Input value={formData.network} onChange={(e) => handleInputChange('network', e.target.value)} className="mt-1.5 font-mono" />
                </div>
                <div>
                  <Label>Máscara *</Label>
                  <Input type="number" value={formData.mask} onChange={(e) => handleInputChange('mask', e.target.value)} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Gateway</Label>
                <Input value={formData.gateway} onChange={(e) => handleInputChange('gateway', e.target.value)} className="mt-1.5 font-mono" />
              </div>
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-sm text-green-600 dark:text-green-400 font-medium">Campos copiados</p>
              </div>
              <div>
                <Label>VLAN</Label>
                <Select value={formData.vlan_id || "none"} onValueChange={(v) => handleInputChange('vlan_id', v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Sin VLAN" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin VLAN</SelectItem>
                    {vlans.map((v) => (<SelectItem key={v.id} value={String(v.id)}>{v.vlan_number} - {v.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Site</Label>
                <Select value={formData.site_id || "none"} onValueChange={(v) => handleInputChange('site_id', v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Sin Site" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin Site</SelectItem>
                    {sites.map((s) => (<SelectItem key={s.id} value={String(s.id)}>{s.site}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Descripción</Label>
                <Input value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} className="mt-1.5" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCopyModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? 'Creando...' : 'Crear Copia'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground py-4">
              ¿Eliminar Subnet <strong>{deleteConfirm?.network}/{deleteConfirm?.mask}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Bar */}
        {canDelete && (
          <BulkDeleteBar
            selectedIds={selectedIds}
            onClear={clearSelection}
            onSuccess={() => { fetchSubnets(); fetchStats(); }}
            endpoint="/ipam/subnets"
            entityName="Subnet"
          />
        )}
      </div>
    </Layout>
  );
};

export default SubnetsPage;
